import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
  IonText,
  IonTextarea,
} from "@ionic/react";
import { usePanelFlow, type PanelConfig } from "components/PanelFlow";
import { checkmark } from "ionicons/icons";
import { isReasonDissatisfied, isReasonOther, isReasonSubNoLongerNeeded } from "model";
import { useAuth } from "pages";
// FIXME: Change this to reference `Icon` from `swing-components`
import { MdArrowForward } from "react-icons/md";
import { Alert, Asterisk, Colors, DataItemView } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { translate } from "~utils";

import styles from "./CancelRequestModal.module.css";
import { CancelPanelKey } from "./constants";
import type { CancelRequestLowLevelPanelProps } from "./types";
import {
  cancelReasons,
  filledCancelReasons,
  filledStartedCancelReasons,
  rovingCancelReasons,
  unfilledCancelReasons,
  unfilledLTRCancelReasons,
} from "./utils";

export function CancelReason({
  state,
  goToPanel,
  setState,
  extraProps,
}: CancelRequestLowLevelPanelProps) {
  const { request, onDismiss } = extraProps;
  const { userInfo } = useAuth();

  let reasonOptions = unfilledCancelReasons;

  if (request?.isFilled()) {
    if (request?.isStarted()) {
      reasonOptions = filledStartedCancelReasons;
    } else {
      reasonOptions = filledCancelReasons;
    }
  } else {
    if (request?.isWaitlistReq()) {
      reasonOptions = unfilledLTRCancelReasons;
    }
  }

  if (userInfo.isSuperAdmin && request?.school.isRoving && request?.isRovingRequest()) {
    reasonOptions = [...reasonOptions, ...rovingCancelReasons];
  }

  return (
    <IonList className={styles.listFill}>
      <IonItem lines="none">
        <IonText>
          {translate("CANCEL_REQ_LABEL_WHY_CANCEL")}
          <Asterisk />
        </IonText>
      </IonItem>
      <IonRadioGroup value={state.cancelReason}>
        {reasonOptions.map((cancelReason) => {
          const cancelText = cancelReasons[cancelReason];
          return (
            <IonItem
              key={cancelReason}
              onClick={() =>
                setState((currentState) => ({
                  ...currentState,
                  cancelReason,
                }))
              }
            >
              <IonLabel>{cancelText}</IonLabel>
              <IonRadio mode="md" slot="start" value={cancelReason} aria-label={cancelText} />
            </IonItem>
          );
        })}
      </IonRadioGroup>
      <div className={styles.pairButtonHolder}>
        <IonButton
          color="primary"
          className="w-full ion-no-margin"
          disabled={!state.cancelReason}
          onClick={() => goToPanel(CancelPanelKey.CancelConfirmPanel)}
        >
          {translate("LABEL_NEXT")}
          <MdArrowForward />
        </IonButton>
        <IonButton
          color="primary"
          fill="outline"
          className="w-full ion-no-margin"
          onClick={() => onDismiss(null, "cancel")}
        >
          {translate("CANCEL_REQ_BUTTON_DO_NOT_CANCEL")}
        </IonButton>
      </div>
    </IonList>
  );
}

export const CancelConfirm: React.FC<CancelRequestLowLevelPanelProps> = ({
  state,
  goToPanel,
  setState,
  extraProps,
}) => {
  const { request, onDismiss, onSubmit, isSubmitting, setIsSubmitting } = extraProps;
  const isCancelReasonOther = isReasonOther(state.cancelReason);
  const isCancelReasonDissatisfied = isReasonDissatisfied(state.cancelReason);
  const isCancelReasonSubNoLongerNeeded = isReasonSubNoLongerNeeded(state.cancelReason);
  const subNoLongerNeededReasons = [
    translate("CANCEL_REQ_SUB_NO_LONGER_NEEDED_AGENCY"),
    translate("CANCEL_REQ_SUB_NO_LONGER_NEEDED_INTERNAL"),
    translate("CANCEL_REQ_SUB_NO_LONGER_NEEDED_PERMANENT"),
    translate("CANCEL_REQ_SUB_NO_LONGER_NEEDED_GENERIC"),
  ];

  const onCancel = async () => {
    setIsSubmitting(true);

    const result = await onSubmit(state);

    if (!result.isSuccess) {
      return goToPanel(CancelPanelKey.CancelErrorPanel);
    }

    if (result.data) {
      setState((currentState) => ({
        ...currentState,
        response: result.data,
      }));
    }

    return goToPanel(CancelPanelKey.CancelSuccessPanel);
  };

  return (
    <IonList className={styles.listFill}>
      {!isCancelReasonDissatisfied && request?.willStillBeInvoiced && (
        <IonItem lines="none">
          <Alert color="danger">
            <span>
              {translate("CANCEL_REQ_LABEL_LAST_MIN_WARNING")}{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href={generateSwingSupportURL(
                  "/hc/en-us/articles/115005318228-What-is-the-Policy-for-Cancellations",
                )}
              >
                {translate("CANCEL_REQ_LABEL_CANCELLATION_POLICY")}
              </a>
              .
            </span>
          </Alert>
        </IonItem>
      )}

      <IonItem lines="none">
        <IonText className="block">
          {isCancelReasonDissatisfied ? (
            translate("CANCEL_REQ_LABEL_APOLOGY")
          ) : (
            <>
              <span className="font-medium">{translate("CANCEL_REQ_LABEL_REASON_SELECTED")}</span>{" "}
              {state?.cancelReason
                ? cancelReasons[state.cancelReason]
                : "Error Code: NO_CANCEL_REASON_TEXT"}
            </>
          )}
        </IonText>
      </IonItem>

      {(isCancelReasonOther || isCancelReasonDissatisfied) && (
        <IonItem>
          <IonLabel position="stacked" className="font-medium">
            {isCancelReasonDissatisfied
              ? translate("CANCEL_REQ_LABEL_WHY_DISSATISFIED")
              : translate("CANCEL_REQ_LABEL_WHAT_OTHER_REASON")}
            <Asterisk />
          </IonLabel>
          <IonTextarea
            mode="md"
            data-testid="cancel_request_modal-other_reason_field"
            placeholder={
              isCancelReasonDissatisfied
                ? translate("CANCEL_REQ_LABEL_ADD_FEEDBACK")
                : translate("CANCEL_REQ_LABEL_ADD_EXPLANATION")
            }
            rows={2}
            autofocus
            onIonInput={(e) =>
              setState((currentState) => ({
                ...currentState,
                cancelExplanation: e.target.value as string,
              }))
            }
          />
        </IonItem>
      )}

      {isCancelReasonSubNoLongerNeeded && (
        <>
          <IonItem lines="none">
            <span css={{ fontWeight: "bold" }}>
              {translate("CANCEL_REQ_SUB_NO_LONGER_NEEDED_WHY")}
            </span>
            <span css={{ color: "var(--ion-color-danger)" }}>*</span>
          </IonItem>
          <IonRadioGroup
            onIonChange={(e) =>
              setState((currentState) => ({
                ...currentState,
                cancelExplanation: e.target.value,
              }))
            }
          >
            {subNoLongerNeededReasons.map((reason) => (
              <IonItem key={reason}>
                <IonRadio labelPlacement="end" justify="start" value={reason}>
                  {reason}
                </IonRadio>
              </IonItem>
            ))}
          </IonRadioGroup>
        </>
      )}

      {isCancelReasonDissatisfied && (
        <>
          <IonItem lines="none">
            <IonLabel>
              {translate("CANCEL_REQ_LABEL_REPOST_REQUEST")}
              <Asterisk />
            </IonLabel>
          </IonItem>
          <IonRadioGroup
            value={
              state.shouldRepost === true ? "yes" : state.shouldRepost === false ? "no" : undefined
            }
          >
            <IonItem>
              <IonLabel>{translate("CANCEL_REQ_LABEL_REPOST_REQUEST_YES")}</IonLabel>
              <IonRadio
                mode="md"
                slot="start"
                value="yes"
                onClick={() =>
                  setState((currentState) => ({
                    ...currentState,
                    shouldRepost: true,
                  }))
                }
              />
            </IonItem>
            <IonItem>
              <IonLabel>{translate("CANCEL_REQ_LABEL_REPOST_REQUEST_NO")}</IonLabel>
              <IonRadio
                mode="md"
                slot="start"
                value="no"
                onClick={() =>
                  setState((currentState) => ({
                    ...currentState,
                    shouldRepost: false,
                  }))
                }
              />
            </IonItem>
          </IonRadioGroup>
        </>
      )}

      <IonItem lines="none">
        <IonLabel>
          <IonText className="block font-medium mb-2 mt-4">
            {translate("CANCEL_REQ_LABEL_CONFIRM_CANCEL")}
          </IonText>
          <IonText>
            {isCancelReasonDissatisfied
              ? translate("CANCEL_REQ_LABEL_NO_CHARGE")
              : translate("CANCEL_REQ_LABEL_GENERAL_WARNING")}
          </IonText>
        </IonLabel>
      </IonItem>

      <div className={styles.pairButtonHolder}>
        <IonButton
          color="primary"
          className="w-full ion-no-margin"
          disabled={
            ((isCancelReasonOther || isCancelReasonDissatisfied) && !state.cancelExplanation) ||
            (isCancelReasonDissatisfied && state.shouldRepost === undefined) ||
            (isCancelReasonSubNoLongerNeeded && !state.cancelExplanation) ||
            isSubmitting
          }
          onClick={onCancel}
        >
          {isSubmitting
            ? translate("CANCEL_REQ_BUTTON_PROCESSING")
            : translate("CANCEL_REQ_BUTTON_CONFIRM_CANCEL")}
          {isSubmitting && <IonSpinner name="dots" />}
        </IonButton>
        <IonButton
          color="primary"
          fill="outline"
          className="w-full ion-no-margin"
          disabled={isSubmitting}
          onClick={() => onDismiss(null, "cancel")}
        >
          {translate("CANCEL_REQ_BUTTON_CONFIRM_DO_NOT_CANCEL")}
        </IonButton>
      </div>
    </IonList>
  );
};

export const CancelSuccess: React.FC<CancelRequestLowLevelPanelProps> = ({ state, extraProps }) => {
  const { onDismiss } = extraProps;
  const { request, response } = state;
  const isReposted = response?.repostedRequestId;
  const isDissatisfiedReason = isReasonDissatisfied(state.cancelReason);

  if (!isDissatisfiedReason) {
    return (
      <IonList className={styles.listFill}>
        <div css={{ display: "flex", justifyContent: "center", padding: "40px 0 24px 0" }}>
          <IonIcon
            aria-hidden="true"
            icon={checkmark}
            css={{ width: "72px", height: "72px", color: Colors.green500 }}
          />
        </div>
        <IonText
          css={{
            fontWeight: 600,
            fontSize: "18px",
            display: "flex",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          {translate("CANCEL_REQ_LABEL_HEADER_CANCELLED")}
        </IonText>
        <IonText
          css={{
            margin: "16px",
          }}
        >
          {translate("CANCEL_REQ_LABEL_CANCELLED_MESSAGE")}
        </IonText>
        <div className={styles.pairButtonHolder}>
          <IonButton
            color="primary"
            className="ion-no-margin w-full"
            onClick={() => {
              onDismiss(null, "cancel");
            }}
          >
            <IonText>{translate("CANCEL_REQ_BUTTON_RETURN_TO_REQUESTS")}</IonText>
          </IonButton>
        </div>
      </IonList>
    );
  }

  return (
    <IonList className={styles.listFill}>
      <div
        css={{
          display: "flex",
          justifyContent: "center",
          padding: "40px 0 24px 0",
        }}
      >
        <IonIcon
          aria-hidden="true"
          icon={checkmark}
          css={{ width: "72px", height: "72px", color: Colors.green500 }}
        />
      </div>

      <IonText
        css={{
          display: "flex",
          justifyContent: "center",
          fontWeight: 600,
          fontSize: "18px",
          marginBottom: "16px",
        }}
      >
        {isReposted
          ? translate("CANCEL_REQ_LABEL_HEADER_REPOSTED")
          : translate("CANCEL_REQ_LABEL_HEADER_CANCELLED")}
      </IonText>

      {isReposted && (
        <IonItem css={{ padding: "8px 0" }}>
          <DataItemView
            stackLayout
            icon="Star"
            css={{
              padding: "8px 0",
            }}
            label={
              <p css={{ margin: 0, fontSize: 16, fontWeight: 400, marginBottom: "16px" }}>
                {translate("CANCEL_REQ_LABEL_REPOSTED_MESSAGE")}
              </p>
            }
          />
        </IonItem>
      )}

      <IonItem css={{ padding: "8px 0" }}>
        <DataItemView
          stackLayout
          icon="Monetization"
          css={{
            padding: "8px 0",
          }}
          label={
            <p css={{ margin: 0, fontSize: 16, fontWeight: 400, marginBottom: "16px" }}>
              {translate("CANCEL_REQ_LABEL_CANCELLED_ORIGINAL_REQUEST")}
            </p>
          }
        />
      </IonItem>

      {!isReposted && (
        <IonItem css={{ padding: "8px 0" }}>
          <DataItemView
            stackLayout
            icon="Search"
            css={{
              padding: "8px 0",
            }}
            label={
              <p css={{ margin: 0, fontSize: 16, fontWeight: 400, marginBottom: "16px" }}>
                {translate("CANCEL_REQ_LABEL_SWING_TO_INVESTIGATE")}
              </p>
            }
          />
        </IonItem>
      )}

      <IonItem lines="none" css={{ padding: "8px 0" }}>
        <DataItemView
          stackLayout
          icon="Close"
          css={{
            padding: "8px 0",
          }}
          label={
            <p css={{ margin: 0, fontSize: 16, fontWeight: 400 }}>
              {translate("CANCEL_REQ_LABEL_SUB_BLOCKED", { subName: request?.sub.name || "Sub" })}
            </p>
          }
        />
      </IonItem>

      <div className={styles.pairButtonHolder}>
        <IonButton
          color="primary"
          className="ion-no-margin w-full"
          onClick={() => {
            onDismiss(state, "cancel");
          }}
        >
          <IonText>
            {isReposted
              ? translate("CANCEL_REQ_BUTTON_VIEW_REQUEST")
              : translate("CANCEL_REQ_BUTTON_RETURN_TO_REQUESTS")}
          </IonText>
        </IonButton>
      </div>
    </IonList>
  );
};

export const CancelError: React.FC<CancelRequestLowLevelPanelProps> = ({ extraProps }) => {
  const { onDismiss } = extraProps;

  return (
    <>
      <div className="mb-4">
        {translate("LABEL_INTERNAL_ERROR", {
          errorCode: "Error Code: CANCEL_REQ_API_ERROR",
        })}
      </div>
      <IonButton
        color="primary"
        className="ion-no-margin"
        onClick={() => onDismiss(null, "cancel")}
      >
        Close
      </IonButton>
    </>
  );
};

const CancelReasonPanel: React.FC = () => {
  return <CancelReason {...usePanelFlow()} />;
};

const CancelConfirmPanel: React.FC = () => {
  return <CancelConfirm {...usePanelFlow()} />;
};

const CancelSuccessPanel: React.FC = () => {
  return <CancelSuccess {...usePanelFlow()} />;
};

const CancelErrorPanel: React.FC = () => {
  return <CancelError {...usePanelFlow()} />;
};

export const panels: PanelConfig[] = [
  { key: CancelPanelKey.CancelReasonPanel, panel: CancelReasonPanel },
  { key: CancelPanelKey.CancelConfirmPanel, panel: CancelConfirmPanel },
  { key: CancelPanelKey.CancelSuccessPanel, panel: CancelSuccessPanel },
  { key: CancelPanelKey.CancelErrorPanel, panel: CancelErrorPanel },
];
